/**
 * Booking Utility Functions
 *
 * Centralized utilities for handling booking operations across the app
 */

/**
 * Check if an error is a booking-related HTTP error (4xx or 5xx)
 * @param {Error} error - The error object from the API call
 * @returns {boolean} - True if error is 4xx or 5xx status code
 */
export const isBookingError = error => {
  const status = error?.response?.status || error?.status;
  if (
    status === 422 ||
    status === 400 ||
    status === 403 ||
    status === 404 ||
    status === 409 ||
    status === 412 ||
    status === 429 ||
    status === 401
  ) {
    return true;
  }
  return status >= 400 && status < 600;
};

/**
 * Check if error is specifically a 4xx client error
 * @param {Error} error - The error object from the API call
 * @returns {boolean} - True if error is 4xx status code
 */
export const isClientError = error => {
  const status = error?.response?.status || error?.status;
  return status >= 400 && status < 500;
};

/**
 * Check if error is specifically a 5xx server error
 * @param {Error} error - The error object from the API call
 * @returns {boolean} - True if error is 5xx status code
 */
export const isServerError = error => {
  const status = error?.response?.status || error?.status;
  return status >= 500 && status < 600;
};

/**
 * Extract user-friendly error message from API error
 * @param {Error} error - The error object from the API call
 * @returns {string} - User-friendly error message
 */
export const getBookingErrorMessage = error => {
  // Check for message in response data
  let backendMessage = error?.response?.data?.message;

  // Check if message is a JSON string that needs parsing (422 error with nested JSON)
  // Example: message: "{\"error\":\"NO_SUITABLE_SUBSCRIPTION\",\"message\":\"...\",\"details\":[...]}"
  if (backendMessage && typeof backendMessage === "string") {
    try {
      const parsed = JSON.parse(backendMessage);
      if (parsed && typeof parsed === "object" && parsed.message) {
        backendMessage = parsed.message;
      }
    } catch (e) {
      // Not valid JSON, use the string as-is
    }
  }

  // Check if message is an object (NO_SUITABLE_SUBSCRIPTION error structure)
  // Structure: { error: "...", message: "...", details: [...] }
  // Extract the nested message property if it exists
  if (
    backendMessage &&
    typeof backendMessage === "object" &&
    !Array.isArray(backendMessage) &&
    backendMessage.message
  ) {
    backendMessage = backendMessage.message;
  }

  if (backendMessage) {
    return backendMessage;
  }

  // Check if response data itself is an object with message
  if (error?.response?.data && typeof error.response.data === "object" && error.response.data.message) {
    return error.response.data.message;
  }

  // Check for message directly on error
  if (error?.message) {
    return error.message;
  }

  // Check if error itself is an object with message
  if (error && typeof error === "object" && error.message) {
    return error.message;
  }

  if (isServerError(error)) {
    return "Server error occurred. Please try again later.";
  }
  if (isClientError(error)) {
    return "Booking request failed. Please check your details and try again.";
  }

  return "An unexpected error occurred. Please try again.";
};

/**
 * Validate booking payload before submission
 * @param {Object} payload - The booking payload
 * @returns {Object} - { isValid: boolean, errors: string[] }
 */
export const validateBookingPayload = payload => {
  const errors = [];

  if (!payload.userId) {
    errors.push("User ID is required");
  }
  if (!payload.spaceId) {
    errors.push("Space ID is required");
  }
  if (!payload.startTime) {
    errors.push("Start time is required");
  }
  if (!payload.endTime) {
    errors.push("End time is required");
  }
  if (payload.startTime && payload.endTime && new Date(payload.startTime) >= new Date(payload.endTime)) {
    errors.push("End time must be after start time");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Determine which booking buttons should be shown based on subscription and error state
 * @param {boolean} hasActiveSubscription - Whether user has active subscription
 * @param {boolean} bookingError - Whether a booking error occurred
 * @returns {Object} - { showBookButton: boolean, showPayButton: boolean }
 */
export const getBookingButtonsVisibility = (hasActiveSubscription, bookingError) => {
  return {
    // Show Book button if: has subscription (always show if user has subscription)
    showBookButton: hasActiveSubscription,
    // Show Pay button if: no subscription OR booking error exists
    showPayButton: !hasActiveSubscription || bookingError,
  };
};

/**
 * Format booking time for display
 * @param {string} isoTime - ISO date string
 * @param {string} timeFormat - '12h' or '24h'
 * @returns {string} - Formatted time string
 */
export const formatBookingTime = (isoTime, timeFormat = "12h") => {
  const date = new Date(isoTime);
  const options = {
    hour: "numeric",
    minute: "2-digit",
    hour12: timeFormat === "12h",
  };
  return date.toLocaleTimeString("en-US", options);
};

/**
 * Calculate booking duration in hours
 * @param {string} startTime - ISO date string
 * @param {string} endTime - ISO date string
 * @returns {number} - Duration in hours
 */
export const calculateBookingDuration = (startTime, endTime) => {
  const start = new Date(startTime);
  const end = new Date(endTime);
  const durationMs = end - start;
  return durationMs / (1000 * 60 * 60); // Convert to hours
};

/**
 * Calculate total booking cost
 * @param {number} hourlyRate - Price per hour
 * @param {string} startTime - ISO date string
 * @param {string} endTime - ISO date string
 * @returns {number} - Total cost
 */
export const calculateBookingCost = (hourlyRate, startTime, endTime) => {
  const hours = calculateBookingDuration(startTime, endTime);
  return hourlyRate * hours;
};
